import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  User, Mail, Phone, MapPin, GraduationCap, Award, 
  CheckCircle, Clock, Star, BookOpen, Loader2, AlertCircle,
  Calendar, Languages, Briefcase, Shield
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import axios from 'axios';

const PublicTutorProfile = () => {
  const { tutorCode } = useParams();
  const [loading, setLoading] = useState(true);
  const [tutor, setTutor] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTutorProfile();
  }, [tutorCode]);

  const loadTutorProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/tutor/${tutorCode}`
      );
      setTutor(response.data.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load tutor profile');
      console.error('Error loading tutor:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-16 w-16 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading tutor profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <AlertCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Tutor Not Found</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">
            Please check the QR code and try again.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header Card */}
        <Card className="p-8 mb-6 bg-white shadow-xl">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Profile Image */}
            <div className="relative">
              <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-blue-500 shadow-lg">
                {tutor.profile_image ? (
                  <img 
                    src={tutor.profile_image} 
                    alt={tutor.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                    <User className="h-16 w-16 text-white" />
                  </div>
                )}
              </div>
              {tutor.is_verified && (
                <div className="absolute -bottom-2 -right-2 bg-green-500 rounded-full p-2 shadow-lg">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
              )}
            </div>

            {/* Basic Info */}
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                <h1 className="text-3xl font-bold text-gray-900">{tutor.name}</h1>
                {tutor.is_verified && (
                  <Badge className="bg-green-500 text-white">
                    <Shield className="h-3 w-3 mr-1" />
                    Verified
                  </Badge>
                )}
              </div>
              
              <p className="text-lg text-gray-600 mb-3">{tutor.headline || 'Professional Tutor'}</p>
              
              <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-4">
                {tutor.experience_years && (
                  <Badge variant="outline" className="text-sm">
                    <Briefcase className="h-3 w-3 mr-1" />
                    {tutor.experience_years}+ years
                  </Badge>
                )}
                {tutor.rating && (
                  <Badge variant="outline" className="text-sm">
                    <Star className="h-3 w-3 mr-1 fill-yellow-400 text-yellow-400" />
                    {tutor.rating}/5
                  </Badge>
                )}
                <Badge variant="outline" className="text-sm">
                  <Calendar className="h-3 w-3 mr-1" />
                  ID: {tutor.tutor_id_code}
                </Badge>
              </div>

              {/* Contact Info */}
              <div className="space-y-2 text-sm">
                {tutor.email && (
                  <div className="flex items-center gap-2 text-gray-600 justify-center md:justify-start">
                    <Mail className="h-4 w-4" />
                    <span>{tutor.email}</span>
                  </div>
                )}
                {tutor.phone && (
                  <div className="flex items-center gap-2 text-gray-600 justify-center md:justify-start">
                    <Phone className="h-4 w-4" />
                    <span>{tutor.phone}</span>
                  </div>
                )}
                {tutor.location && (
                  <div className="flex items-center gap-2 text-gray-600 justify-center md:justify-start">
                    <MapPin className="h-4 w-4" />
                    <span>{tutor.location}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* About Section */}
        {tutor.bio && (
          <Card className="p-6 mb-6">
            <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
              <User className="h-5 w-5 text-blue-600" />
              About Me
            </h2>
            <p className="text-gray-700 leading-relaxed">{tutor.bio}</p>
          </Card>
        )}

        {/* Education & Qualifications */}
        {tutor.qualifications && tutor.qualifications.length > 0 && (
          <Card className="p-6 mb-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-blue-600" />
              Education & Qualifications
            </h2>
            <div className="space-y-3">
              {tutor.qualifications.map((qual, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                  <Award className="h-5 w-5 text-blue-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">{qual.degree}</h3>
                    <p className="text-sm text-gray-600">{qual.institution}</p>
                    {qual.year && (
                      <p className="text-xs text-gray-500 mt-1">{qual.year}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Subjects */}
        {tutor.subjects && tutor.subjects.length > 0 && (
          <Card className="p-6 mb-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-blue-600" />
              Subjects I Teach
            </h2>
            <div className="flex flex-wrap gap-2">
              {tutor.subjects.map((subject, index) => (
                <Badge key={index} className="bg-blue-100 text-blue-800 hover:bg-blue-200">
                  {subject}
                </Badge>
              ))}
            </div>
          </Card>
        )}

        {/* Languages */}
        {tutor.languages && tutor.languages.length > 0 && (
          <Card className="p-6 mb-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Languages className="h-5 w-5 text-blue-600" />
              Languages
            </h2>
            <div className="flex flex-wrap gap-2">
              {tutor.languages.map((language, index) => (
                <Badge key={index} variant="outline">
                  {language}
                </Badge>
              ))}
            </div>
          </Card>
        )}

        {/* Verification Status */}
        <Card className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <div className="flex items-center gap-3">
            <div className="bg-green-500 rounded-full p-3">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-green-900">Verified PriEdu Tutor</h3>
              <p className="text-sm text-green-700">
                This tutor has been verified and approved by PriEdu. All credentials have been checked.
              </p>
            </div>
          </div>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>Powered by <span className="font-semibold text-blue-600">PriEdu</span></p>
          <p className="mt-1">Professional Tutoring Services</p>
        </div>
      </div>
    </div>
  );
};

export default PublicTutorProfile;
